import { z } from 'zod';
import { Merge } from '@mergeapi/merge-node-client';

export const MergeCategoryEnum = z.enum([
  'hris',
  'ats',
  'accounting',
  'ticketing',
  'crm',
  'mktg',
  'filestorage',
]);

export const CategoryEnum: z.ZodType<Merge.hris.CategoryEnum> =
  MergeCategoryEnum;

export type MergeCategoryEnum = z.infer<typeof MergeCategoryEnum>;

export const MergeLinkToken = z.object({
  linkToken: z.string(),
  magicLinkUrl: z.string().url(),
  category: MergeCategoryEnum,
});

export type MergeLinkToken = z.infer<typeof MergeLinkToken>;

export const MergeCategoryEnums = Object.values(MergeCategoryEnum.Values);

export const getMergeCategoryEnumForString = (
  value: string,
): MergeCategoryEnum => {
  return (
    MergeCategoryEnums.find((enumValue) => enumValue === value) ||
    MergeCategoryEnums[0]
  );
};

export const MergeAccountToken = z.object({
  accountToken: z.string(),
});

export type MergeAccountToken = z.infer<typeof MergeAccountToken>;

export const MergeAttachments = z.array(
  z.object({
    id: z.string(),
    fileName: z.string().nullish(),
    fileUrl: z.string().nullish(),
    attachmentType: z.string().nullish(),
    candidate: z
      .object({
        id: z.string(),
        remote_created_at: z.string().datetime().nullish(),
      })
      .nullish(),
  }),
);

export type MergeAttachments = z.infer<typeof MergeAttachments>;

export const MergeEmployee = z.object({
  id: z.string(),
  remoteId: z.string().nullish(),
});

export type MergeEmployee = z.infer<typeof MergeEmployee>;

export const MergeEmployees = z.array(MergeEmployee);

export type MergeEmployees = z.infer<typeof MergeEmployees>;

export const MergeJob = z.object({
  id: z.string(),
  remoteId: z.string().nullish(),
  name: z.string().nullish(),
  fieldMappings: z.record(z.string(), z.unknown()).nullish(),
});

export type MergeJob = z.infer<typeof MergeJob>;

export const MergeJobInterviewStage = z.object({
  id: z.string(),
  remoteId: z.string().nullish(),
  name: z.string().nullish(),
  job: z.string().nullish(),
});

export type MergeJobInterviewStage = z.infer<typeof MergeJobInterviewStage>;

export const MergeApplication = z.object({
  id: z.string(),
  appliedAt: z.string().datetime().nullish(),
  candidate: z
    .object({
      id: z.string(),
      remoteCreatedAt: z.string().datetime().nullish(),
    })
    .nullish(),
  job: MergeJob.nullish(),
  currentStage: z.string().nullish(),
});

export type MergeApplication = z.infer<typeof MergeApplication>;

export const MergeApplications = z.array(MergeApplication);

export type MergeApplications = z.infer<typeof MergeApplications>;

export const MergeCandidates = z.array(
  z.object({
    id: z.string(),
    remoteId: z.string().nullish(),
    createdAt: z.string().datetime().nullish(),
    modifiedAt: z.string().datetime().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    remoteCreatedAt: z.string().datetime().nullish(),
    remoteUpdatedAt: z.string().datetime().nullish(),
    lastInteractionAt: z.string().datetime().nullish(),
    canEmail: z.boolean().nullish(),
    isPrivate: z.boolean().nullish(),
    locations: z.array(z.string()).nullish(),
    phoneNumbers: z
      .array(
        z.object({
          createdAt: z.string().datetime().nullish(),
          modifiedAt: z.string().datetime().nullish(),
          value: z.string(),
          remoteWasDeleted: z.boolean().nullish(),
        }),
      )
      .nullish(),
    emailAddresses: z
      .array(
        z.object({
          createdAt: z.string().datetime().nullish(),
          modifiedAt: z.string().datetime().nullish(),
          value: z.string(),
          remoteWasDeleted: z.boolean().nullish(),
        }),
      )
      .nullish(),
    urls: z.array(z.string()).nullish(),
    tags: z.array(z.string()).nullish(),
    applications: z.array(z.string()).nullish(),
    attachments: z.array(z.string()).nullish(),
    remoteWasDeleted: z.boolean().nullish(),
    fieldMappings: z
      .object({
        organization_defined_targets: z.record(z.any()).nullish(),
        linked_account_defined_targets: z.record(z.any()).nullish(),
      })
      .nullish(),
  }),
);

export type MergeCandidates = z.infer<typeof MergeCandidates>;
